import { useCallback, useEffect, useState } from 'react';

import { TIME_CONSTANTS } from '../../../constants/time';
import { type createStorageAPI, STORAGE_KEYS } from '../../../utils/typed-storage';

export interface CrossTabSyncReturn {
	lastSessionExpiry: number | null;
}

const parseValidTimestamp = (jsonValue: string): number | null => {
	try {
		const parsed = JSON.parse(jsonValue);
		return typeof parsed === 'number' && parsed >= 0 ? parsed : null;
	} catch {
		return null;
	}
};

export const useCrossTabSync = (
	sessionExpiryTimestamp: number | null,
	storageAPI: ReturnType<typeof createStorageAPI>,
): CrossTabSyncReturn => {
	const [lastSessionExpiry, setLastSessionExpiry] = useState<number | null>(() => {
		const result = storageAPI.heartbeatSessionExpiry.get();
		return result.success ? result.data : null;
	});

	useEffect(() => {
		if (sessionExpiryTimestamp == null) {
			return;
		}

		setLastSessionExpiry((current) => {
			if (!current) {
				storageAPI.heartbeatSessionExpiry.set(sessionExpiryTimestamp);
				return sessionExpiryTimestamp;
			}

			const isSessionExtension = sessionExpiryTimestamp > current;
			const isReauthentication =
				current - sessionExpiryTimestamp > TIME_CONSTANTS.REAUTH_DETECTION_THRESHOLD_MS;

			if (isSessionExtension || isReauthentication) {
				storageAPI.heartbeatSessionExpiry.set(sessionExpiryTimestamp);
				return sessionExpiryTimestamp;
			}
			return current;
		});
	}, [sessionExpiryTimestamp, storageAPI]);

	const handleCrossTabUpdate = useCallback((event: StorageEvent) => {
		if (event.key === STORAGE_KEYS.HEARTBEAT_SESSION_EXPIRY && event.newValue) {
			const incomingTimestamp = parseValidTimestamp(event.newValue);

			if (incomingTimestamp) {
				setLastSessionExpiry(incomingTimestamp);
			}
		}
	}, []);

	useEffect(() => {
		window.addEventListener('storage', handleCrossTabUpdate);
		return () => {
			window.removeEventListener('storage', handleCrossTabUpdate);
		};
	}, [handleCrossTabUpdate]);

	return { lastSessionExpiry };
};
